import { FirebaseError } from '@firebase/app'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { storage, db } from '@/config/firebase'
import { ref, uploadBytes, getDownloadURL, deleteObject } from '@firebase/storage'
import { doc, updateDoc, arrayUnion, getDoc, collection, addDoc, query, where, getDocs } from '@firebase/firestore'
import { useInventory } from './useInventory'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { Vehicle, Invoice, InvoiceStatus } from '@otw/models'

export interface Document {
  id: string
  name: string
  type: 'title_copy' | 'purchase_order' | 'payment_copy' | 'invoice' | 'additional_doc' | 'title_flip'
  url: string
  uploadedAt: string
  uploadedBy: string
  // Additional fields for invoices
  documentId?: string
  clientName?: string
  templateName?: string
}

export interface DocumentUploadResult {
  url: string
  path: string
  name: string
}

export type DocumentUploadType =
  | 'title_copy'
  | 'purchase_order'
  | 'payment_copy'
  | 'additional_doc'
  | 'title_flip'

export interface VehicleDocumentEmailInfo {
  id: string
  stockNumber?: string | null
  vin?: string | null
}

export interface BulkDocumentUploadResult {
  succeeded: Array<{ vehicleId: string; result: DocumentUploadResult }>
  failed: Array<{ vehicleId: string; error: string }>
}

const fetchDocumentsForInventoryObject = async (
  collectionName: 'master_inventory' | 'shield_inventory',
  inventoryObjectId: string,
): Promise<Document[]> => {
  try {
    const docRef = doc(db, collectionName, inventoryObjectId)
    const docSnap = await getDoc(docRef)
    
    if (!docSnap.exists()) {
      console.error('No such document!')
      return []
    }

    const data = docSnap.data() as Vehicle
    const documents: Document[] = []
    
    // Add title copies
    data.documents?.title_copies?.forEach((url: string, index: number) => {
      const decodedUrl = decodeURIComponent(url)
      const fileName = decodedUrl.split('/').pop()?.split('?')[0] || `Title Copy ${index + 1}`
      documents.push({
        id: `title_${index}`,
        name: fileName,
        type: 'title_copy',
        url,
        uploadedAt: new Date().toISOString(),
        uploadedBy: 'Unknown'
      })
    })
    
    // Add purchase orders (can be strings for manually uploaded or objects for generated)
    data.documents?.purchase_orders?.forEach((item: string | any, index: number) => {
      // Handle both string URLs (manually uploaded) and objects (generated)
      if (typeof item === 'string') {
        const decodedUrl = decodeURIComponent(item)
        const fileName = decodedUrl.split('/').pop()?.split('?')[0] || `Purchase Order ${index + 1}`
        documents.push({
          id: `po_${index}`,
          name: fileName,
          type: 'purchase_order',
          url: item,
          uploadedAt: new Date().toISOString(),
          uploadedBy: 'Unknown'
        })
      } else {
        // It's an object (generated purchase order)
        documents.push({
          id: `po_${index}`,
          name: item.name || `Purchase Order ${index + 1}`,
          type: 'purchase_order',
          url: item.url,
          uploadedAt: item.generatedAt || new Date().toISOString(),
          uploadedBy: 'System',
          documentId: item.documentId,
          clientName: item.clientName,
          templateName: item.templateName
        })
      }
    })
    
    // Add payment copies
    data.documents?.payment_copies?.forEach((url: string, index: number) => {
      const decodedUrl = decodeURIComponent(url)
      const fileName = decodedUrl.split('/').pop()?.split('?')[0] || `Payment Copy ${index + 1}`
      documents.push({
        id: `payment_${index}`,
        name: fileName,
        type: 'payment_copy',
        url,
        uploadedAt: new Date().toISOString(),
        uploadedBy: 'Unknown'
      })
    })
    
    // Add invoices
    data.documents?.invoices?.forEach((invoice: any, index: number) => {
      documents.push({
        id: `invoice_${index}`,
        name: invoice.name || `Invoice ${index + 1}`,
        type: 'invoice',
        url: invoice.url,
        uploadedAt: invoice.generatedAt || new Date().toISOString(),
        uploadedBy: 'System',
        documentId: invoice.documentId,
        clientName: invoice.clientName,
        templateName: invoice.templateName
      })
    })
    
    // Add additional docs
    data.documents?.additional_docs?.forEach((url: string, index: number) => {
      const decodedUrl = decodeURIComponent(url)
      const fileName = decodedUrl.split('/').pop()?.split('?')[0] || `Additional Doc ${index + 1}`
      documents.push({
        id: `additional_${index}`,
        name: fileName,
        type: 'additional_doc',
        url,
        uploadedAt: new Date().toISOString(),
        uploadedBy: 'Unknown'
      })
    })

    // Add title flip docs
    data.documents?.title_flip_docs?.forEach((url: string, index: number) => {
      const decodedUrl = decodeURIComponent(url)
      const fileName = decodedUrl.split('/').pop()?.split('?')[0] || `Title Flip ${index + 1}`
      documents.push({
        id: `title_flip_${index}`,
        name: fileName,
        type: 'title_flip',
        url,
        uploadedAt: new Date().toISOString(),
        uploadedBy: 'Unknown'
      })
    })

    return documents
  } catch (error) {
    if (error instanceof FirebaseError) {
      console.error(`Error fetching documents for ${inventoryObjectId}:`, error)
    } else {
      console.error(`Error fetching documents for ${inventoryObjectId}:`, error)
    }
    return []
  }
}

const uploadVehicleDocument = async (
  inventoryCollection: 'master_inventory' | 'shield_inventory',
  vehicleId: string,
  file: File,
  documentType: 'title_copy' | 'purchase_order' | 'payment_copy' | 'additional_doc' | 'title_flip',
  options?: { titleState?: string | null },
): Promise<DocumentUploadResult> => {
  try {
    // Create the storage path: {inventory_collection}/{docId}/documents/{document_name.extension}
    const fileExtension = file.name.split('.').pop()
    const fileName = `${file.name}`
    const storagePath = `${inventoryCollection}/${vehicleId}/documents/${fileName}`
    
    // Create storage reference
    const storageRef = ref(storage, storagePath)
    
    // Upload file to Firebase Storage
    const snapshot = await uploadBytes(storageRef, file)
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref)
    
    // Update Firestore document
    const vehicleDocRef = doc(db, inventoryCollection, vehicleId)
    
    // Get current document data to see what's there
    const currentDoc = await getDoc(vehicleDocRef)
    
    // Determine which array to update based on document type
    const fieldPath = 
      documentType === 'title_copy' 
        ? 'documents.title_copies' 
        : documentType === 'purchase_order'
        ? 'documents.purchase_orders'
        : documentType === 'payment_copy'
        ? 'documents.payment_copies'
        : documentType === 'title_flip'
        ? 'documents.title_flip_docs'
        : 'documents.additional_docs'
    
    // Check if documents field exists, if not initialize it
    const currentData = currentDoc.data()
    const updateData: any = {}
    
    if (!currentData?.documents) {
      // Initialize documents structure
      updateData.documents = {
        title_copies: documentType === 'title_copy' ? [downloadURL] : [],
        purchase_orders: documentType === 'purchase_order' ? [downloadURL] : [],
        payment_copies: documentType === 'payment_copy' ? [downloadURL] : [],
        additional_docs: documentType === 'additional_doc' ? [downloadURL] : [],
        title_flip_docs: documentType === 'title_flip' ? [downloadURL] : []
      }
    } else {
      // Use arrayUnion on existing structure
      updateData[fieldPath] = arrayUnion(downloadURL)
    }

    if (documentType === 'title_copy' && options?.titleState) {
      updateData.title_state = options.titleState
    }
    
    // Update the document with the new file URL
    await updateDoc(vehicleDocRef, updateData)
    
    return {
      url: downloadURL,
      path: storagePath,
      name: fileName
    }
  } catch (error) {
    console.error('Error uploading document:', error)
    throw new Error(`Failed to upload document: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

/**
 * Convert a file URL to base64 string for email attachment
 */
const urlToBase64 = async (url: string): Promise<string> => {
  try {
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(`Failed to fetch file: ${response.statusText}`)
    }
    
    const blob = await response.blob()
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        const base64String = reader.result as string
        // Remove the data:...;base64, prefix if it exists
        const base64Data = base64String.split(',')[1] || base64String
        resolve(base64Data)
      }
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  } catch (error) {
    console.error('Error converting URL to base64:', error)
    throw error
  }
}

const formatVehicleEmailLabel = (vehicle: VehicleDocumentEmailInfo): string => {
  const parts: string[] = []
  if (vehicle.stockNumber) parts.push(`Stock: ${vehicle.stockNumber}`)
  if (vehicle.vin) parts.push(`VIN: ${vehicle.vin}`)
  return parts.length > 0 ? parts.join(' · ') : vehicle.id
}

/**
 * Send email with attachment based on environment
 * STG -> jack@freetech.co
 * PROD -> mike@optictruckworks.com
 * Supports one or many vehicles (single notification for multi-vehicle wire copies).
 */
const sendPaymentCopyEmail = async (
  documentUrl: string,
  fileName: string,
  vehicles: VehicleDocumentEmailInfo[],
): Promise<void> => {
  try {
    // Determine recipient based on hostname (more reliable than env vars in frontend)
    const isProduction = typeof window !== 'undefined' && 
      !window.location.hostname.includes('stg') && 
      window.location.hostname !== 'localhost'
    const recipientEmail = isProduction 
      ? 'mike@optictruckworks.com' 
      : 'jack@freetech.co' // Default to STG or jack@freetech.co
    
    // Convert file to base64
    const base64Data = await urlToBase64(documentUrl)
    
    // Extract file extension for proper filename
    const fileExtension = fileName.split('.').pop() || 'pdf'
    const primaryId = vehicles[0]?.id || 'unknown'
    const attachmentFileName = fileName || `payment_copy_${primaryId}.${fileExtension}`

    const vehicleLabels = vehicles.map(formatVehicleEmailLabel)
    const isMultiVehicle = vehicles.length > 1
    const vehicleSummary = isMultiVehicle
      ? `${vehicles.length} vehicles`
      : vehicleLabels[0]
        ? `vehicle (${vehicleLabels[0]})`
        : 'vehicle'
    const vehicleListHtml = isMultiVehicle
      ? `<ul style="font-size: 16px; color: #333333; line-height: 1.6; margin-bottom: 16px; padding-left: 20px;">
          ${vehicleLabels.map(label => `<li>${label}</li>`).join('')}
        </ul>`
      : ''
    const vehicleListText = isMultiVehicle
      ? `\n\nVehicles:\n${vehicleLabels.map(label => `- ${label}`).join('\n')}`
      : ''
    
    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Payment Copy Uploaded</title>
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
        </head>
        <body style="font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif; background-color: #f5f5f5; margin: 0; padding: 0;">
          <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); margin-top: 40px; margin-bottom: 40px; padding: 32px;">
            <h1 style="font-size: 24px; font-weight: 500; color: #1976d2; margin-bottom: 24px;">
              Payment Copy Uploaded
            </h1>
            <p style="font-size: 16px; color: #333333; line-height: 1.6; margin-bottom: 16px;">
              A new payment copy has been uploaded for ${vehicleSummary}.
            </p>
            ${vehicleListHtml}
            <p style="font-size: 16px; color: #333333; line-height: 1.6; margin-bottom: 16px;">
              Please find the attached payment copy document.
            </p>
            <p style="font-size: 14px; color: #666666; line-height: 1.6; border-top: 1px solid #e0e0e0; padding-top: 24px; margin-top: 24px;">
              This is an automated notification from the Optic Truck Works Portal.
            </p>
          </div>
        </body>
      </html>
    `
    
    // Prepare the email data
    const emailData = {
      to: recipientEmail,
      message: {
        subject: isMultiVehicle
          ? `Payment Copy Uploaded (${vehicles.length} vehicles)`
          : `Payment Copy Uploaded${vehicleLabels[0] ? ` (${vehicleLabels[0]})` : ''}`,
        html,
        text: `A new payment copy has been uploaded for ${vehicleSummary}.${vehicleListText}\n\nPlease find the attached payment copy document.`,
        attachments: [
          {
            filename: attachmentFileName,
            content: base64Data,
            encoding: 'base64'
          }
        ]
      }
    }
    
    // Add the email to the mail collection
    await addDoc(collection(db, 'mail'), emailData)
    
    console.log(`Payment copy email sent to ${recipientEmail}:`, {
      fileName: attachmentFileName,
      vehicleIds: vehicles.map(v => v.id),
      environment: isProduction ? 'PROD' : 'STG'
    })
  } catch (error) {
    console.error('Error sending payment copy email:', error)
    // Don't throw - we don't want to fail the upload if email fails
  }
}

const uploadVehicleDocumentsToMany = async (
  inventoryCollection: 'master_inventory' | 'shield_inventory',
  vehicleIds: string[],
  file: File,
  documentType: DocumentUploadType,
): Promise<BulkDocumentUploadResult> => {
  const succeeded: BulkDocumentUploadResult['succeeded'] = []
  const failed: BulkDocumentUploadResult['failed'] = []

  for (const vehicleId of vehicleIds) {
    try {
      const result = await uploadVehicleDocument(inventoryCollection, vehicleId, file, documentType)
      succeeded.push({ vehicleId, result })
    } catch (error) {
      failed.push({
        vehicleId,
        error: error instanceof Error ? error.message : 'Unknown error',
      })
    }
  }

  return { succeeded, failed }
}

export const saveVehicleInvoice = async (
  inventoryCollection: 'master_inventory' | 'shield_inventory',
  vehicleId: string,
  invoice: {
    url: string
    documentId: string
    name: string
    clientName?: string
    templateName?: string
  }
): Promise<void> => {
  try {
    const vehicleDocRef = doc(db, inventoryCollection, vehicleId)
    const currentDoc = await getDoc(vehicleDocRef)
    
    if (!currentDoc.exists()) {
      throw new Error('Vehicle document not found')
    }
    
    const currentData = currentDoc.data()
    const invoiceData = {
      ...invoice,
      generatedAt: new Date().toISOString()
    }
    
    const updateData: any = {}
    
    // Determine where to save based on template name
    const isPurchaseOrder = invoice.templateName === 'Purchase Order'
    
    if (!currentData?.documents) {
      if (isPurchaseOrder) {
        updateData.documents = {
          purchase_orders: [invoiceData]
        }
      } else {
        updateData.documents = {
          invoices: [invoiceData]
        }
      }
    } else {
      // Use arrayUnion to add the invoice/purchase order
      if (isPurchaseOrder) {
        updateData['documents.purchase_orders'] = arrayUnion(invoiceData)
      } else {
        updateData['documents.invoices'] = arrayUnion(invoiceData)
      }
    }
    
    await updateDoc(vehicleDocRef, updateData)
  } catch (error) {
    console.error('Error saving invoice:', error)
    throw new Error(`Failed to save invoice: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

const deleteVehicleDocument = async (
  inventoryCollection: 'master_inventory' | 'shield_inventory',
  vehicleId: string,
  documentUrl: string,
  documentType: 'title_copy' | 'purchase_order' | 'payment_copy' | 'additional_doc' | 'title_flip'
): Promise<void> => {
  try {
    // Get current document data
    const vehicleDocRef = doc(db, inventoryCollection, vehicleId)
    
    // Determine which array to update based on document type
    const fieldPath = 
      documentType === 'title_copy' 
        ? 'documents.title_copies' 
        : documentType === 'purchase_order'
        ? 'documents.purchase_orders'
        : documentType === 'payment_copy'
        ? 'documents.payment_copies'
        : documentType === 'title_flip'
        ? 'documents.title_flip_docs'
        : 'documents.additional_docs'
    
    // Remove the URL from the array
    const docSnap = await getDoc(vehicleDocRef)
    if (docSnap.exists()) {
      const data = docSnap.data()
      const arrayKey = 
        documentType === 'title_copy' 
          ? 'title_copies' 
          : documentType === 'purchase_order'
          ? 'purchase_orders'
          : documentType === 'payment_copy'
          ? 'payment_copies'
          : documentType === 'title_flip'
          ? 'title_flip_docs'
          : 'additional_docs'
      const currentArray = data?.documents?.[arrayKey] || []
      // Handle both string URLs and object documents (for purchase orders)
      const updatedArray = currentArray.filter((item: string | any) => {
        if (typeof item === 'string') {
          return item !== documentUrl
        } else if (typeof item === 'object' && item !== null) {
          // For objects (generated purchase orders), compare the url property
          return item.url !== documentUrl
        }
        return true // Keep items that don't match the expected types
      })
      
      await updateDoc(vehicleDocRef, {
        [fieldPath]: updatedArray
      })
    }
    
    // Delete from Firebase Storage (only for manually uploaded files, not generated documents)
    // Generated purchase orders are stored in PandaDoc, not Firebase Storage
    try {
      // Check if this is a Firebase Storage URL (manually uploaded) or a PandaDoc URL (generated)
      const isFirebaseStorageUrl = documentUrl.includes('firebasestorage.googleapis.com') || 
                                   documentUrl.includes('firebase') ||
                                   (!documentUrl.includes('pandadoc.com') && !documentUrl.includes('app.pandadoc.com'))
      
      if (isFirebaseStorageUrl) {
      // Extract the filename from the URL
      const decodedUrl = decodeURIComponent(documentUrl)
      const fileName = decodedUrl.split('/').pop()?.split('?')[0]
      
      if (fileName) {
        // Construct the storage path: {inventory_collection}/{docId}/documents/{document_name.extension}
        const storagePath = `${inventoryCollection}/${vehicleId}/documents/${fileName}`
        const storageRef = ref(storage, storagePath)
        
        // Delete the file from Firebase Storage
        await deleteObject(storageRef)
        }
      } else {
        // This is a generated document (PandaDoc), no need to delete from Firebase Storage
        console.log('Skipping Firebase Storage deletion for generated document:', documentUrl)
      }
    } catch (storageError) {
      console.warn('Failed to delete file from Firebase Storage:', storageError)
      // Don't throw here - we still want to remove the URL from Firestore even if storage deletion fails
    }
    
  } catch (error) {
    console.error('Error deleting document:', error)
    throw new Error(`Failed to delete document: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

export const useDocumentsForInventoryObject = (
  collectionName: 'master_inventory' | 'shield_inventory',
  inventoryObjectId: string,
  options?: { enabled?: boolean }
) => {
  const queryClient = useQueryClient()
  const { showSuccess, showError, showInfo, showWarning } = useSnackbar()
  const { updateDocument } = useInventory(collectionName, undefined, { dontShowUpdates: true })
  
  const queryKey = ['documents', collectionName, inventoryObjectId]

  const query = useQuery({
    queryKey,
    queryFn: () => fetchDocumentsForInventoryObject(collectionName, inventoryObjectId),
    enabled: options?.enabled !== false,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
  })

  const invalidateDocumentsForVehicles = (vehicleIds: string[]) => {
    vehicleIds.forEach(vehicleId => {
      queryClient.invalidateQueries({ queryKey: ['documents', collectionName, vehicleId] })
    })
  }

  const notifyPaymentCopyEmail = async (
    documentUrl: string,
    fileName: string,
    vehicles: VehicleDocumentEmailInfo[],
  ) => {
    try {
      await sendPaymentCopyEmail(documentUrl, fileName, vehicles)
      const isProduction = !window.location.hostname.includes('stg') && window.location.hostname !== 'localhost'
      const recipientEmail = isProduction ? 'mike@optictruckworks.com' : 'jack@freetech.co'
      showInfo(`Payment copy email sent to ${recipientEmail}`)
    } catch (error) {
      console.error('Failed to send payment copy email:', error)
      // Don't show error to user - email failure shouldn't block the upload
    }
  }

  const uploadDocumentMutation = useMutation({
    mutationFn: async ({
      file,
      documentType,
      titleState,
    }: {
      file: File
      documentType: DocumentUploadType
      titleState?: string | null
    }) => {
      return await uploadVehicleDocument(collectionName, inventoryObjectId, file, documentType, {
        titleState,
      })
    },
    onSuccess: async (result, variables) => {
      queryClient.invalidateQueries({ queryKey })
      showSuccess('Document uploaded successfully!')
      
      // Automatically email payment copies
      if (variables.documentType === 'payment_copy') {
        const vehicleDocRef = doc(db, collectionName, inventoryObjectId)
        const vehicleDoc = await getDoc(vehicleDocRef)
        const vehicleData = vehicleDoc.data() as Vehicle | undefined

        await notifyPaymentCopyEmail(result.url, result.name, [
          {
            id: inventoryObjectId,
            stockNumber: vehicleData?.stock_number,
            vin: vehicleData?.vin,
          },
        ])
      }
    },
    onError: (error) => {
      showError(`Failed to upload document: ${error instanceof Error ? error.message : 'Unknown error'}`)
    },
  })

  const uploadDocumentToVehiclesMutation = useMutation({
    mutationFn: async ({
      file,
      documentType,
      vehicleIds,
      vehiclesMeta,
    }: {
      file: File
      documentType: DocumentUploadType
      vehicleIds: string[]
      vehiclesMeta?: VehicleDocumentEmailInfo[]
    }) => {
      const uniqueVehicleIds = [...new Set(vehicleIds.filter(Boolean))]
      if (uniqueVehicleIds.length === 0) {
        throw new Error('At least one vehicle is required')
      }

      const result = await uploadVehicleDocumentsToMany(
        collectionName,
        uniqueVehicleIds,
        file,
        documentType,
      )

      return { ...result, documentType, vehiclesMeta: vehiclesMeta || [] }
    },
    onSuccess: async ({ succeeded, failed, documentType, vehiclesMeta }) => {
      invalidateDocumentsForVehicles(succeeded.map(item => item.vehicleId))

      const total = succeeded.length + failed.length
      if (failed.length === 0) {
        showSuccess(
          total === 1
            ? 'Document uploaded successfully!'
            : `Document uploaded to ${succeeded.length} vehicles successfully!`,
        )
      } else if (succeeded.length === 0) {
        showError(`Failed to upload document to all ${failed.length} vehicles.`)
      } else {
        showWarning(`Uploaded to ${succeeded.length} of ${total} vehicles. Some uploads failed.`)
      }

      if (documentType === 'payment_copy' && succeeded.length > 0) {
        const metaById = new Map(vehiclesMeta.map(vehicle => [vehicle.id, vehicle]))
        const emailVehicles: VehicleDocumentEmailInfo[] = succeeded.map(({ vehicleId }) => {
          const meta = metaById.get(vehicleId)
          return meta || { id: vehicleId }
        })

        await notifyPaymentCopyEmail(
          succeeded[0].result.url,
          succeeded[0].result.name,
          emailVehicles,
        )
      }
    },
    onError: (error) => {
      showError(`Failed to upload document: ${error instanceof Error ? error.message : 'Unknown error'}`)
    },
  })

  const deleteDocumentMutation = useMutation({
    mutationFn: async ({ documentUrl, documentType }: { documentUrl: string; documentType: DocumentUploadType }) => {
      return await deleteVehicleDocument(collectionName, inventoryObjectId, documentUrl, documentType)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      showSuccess('Document deleted successfully!')
    },
    onError: (error) => {
      showError(`Failed to delete document: ${error instanceof Error ? error.message : 'Unknown error'}`)
    },
  })

  const saveInvoiceMutation = useMutation({
    mutationFn: async (invoice: {
      url: string
      documentId: string
      name: string
      clientName?: string
      templateName?: string
    }) => {
      return await saveVehicleInvoice(collectionName, inventoryObjectId, invoice)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      showSuccess('Invoice saved successfully!')
    },
    onError: (error) => {
      showError(`Failed to save invoice: ${error instanceof Error ? error.message : 'Unknown error'}`)
    },
  })

  const downloadDocument = async (document: Document): Promise<boolean> => {
    try {
      const response = await fetch(document.url)
      if (!response.ok) {
        throw new Error(`Failed to fetch document: ${response.statusText}`)
      }
      
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = window.document.createElement('a')
      link.href = url
      link.download = document.name
      window.document.body.appendChild(link)
      link.click()
      window.document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      showSuccess('Document downloaded successfully!')
      return true
    } catch (error) {
      console.error('Error downloading document:', error)
      showError(`Failed to download document: ${error instanceof Error ? error.message : 'Unknown error'}`)
      return false
    }
  }

  const viewDocument = (document: Document): void => {
    window.open(document.url, '_blank')
  }

  return {
    ...query,
    documents: query.data || [],
    uploadDocument: uploadDocumentMutation.mutate,
    uploadDocumentToVehicles: uploadDocumentToVehiclesMutation.mutate,
    deleteDocument: deleteDocumentMutation.mutate,
    saveInvoice: saveInvoiceMutation.mutate,
    saveInvoiceAsync: saveInvoiceMutation.mutateAsync,
    downloadDocument,
    viewDocument,
    isUploading: uploadDocumentMutation.isPending || uploadDocumentToVehiclesMutation.isPending,
    isDeleting: deleteDocumentMutation.isPending,
    isSavingInvoice: saveInvoiceMutation.isPending,
    uploadDocumentAsync: uploadDocumentMutation.mutateAsync,
    uploadDocumentToVehiclesAsync: uploadDocumentToVehiclesMutation.mutateAsync,
    deleteDocumentAsync: deleteDocumentMutation.mutateAsync,
  }
}